import { canonicalJson } from './query.ts'
import { ApiProblemError } from './problem.ts'

/**
 * A key belongs to a payload, not to a form. Replaying the same payload replays the
 * stored response; a changed payload must get a new key, or the server answers the
 * edit with a 422 about idempotency rather than about the edit.
 */
export interface IdempotencyKeys {
  forPayload(payload: unknown): string
  forget(payload: unknown): void
  clear(): void
}

export function createIdempotencyKeys(newKey: () => string = () => crypto.randomUUID()): IdempotencyKeys {
  const keys = new Map<string, string>()
  const fingerprint = (payload: unknown): string => canonicalJson(payload ?? null)
  return {
    forPayload(payload) {
      const id = fingerprint(payload)
      const existing = keys.get(id)
      if (existing !== undefined) return existing
      const created = newKey()
      keys.set(id, created)
      return created
    },
    forget(payload) {
      keys.delete(fingerprint(payload))
    },
    clear() {
      keys.clear()
    },
  }
}

/**
 * A final 4xx is an answer, not a pending state: a later attempt must send a fresh request
 * instead of replaying it. Network errors, 5xx and a still-in-flight 409 keep the key.
 */
export function forgetOnFinalClientError(keys: IdempotencyKeys, payload: unknown, error: unknown): void {
  if (error instanceof ApiProblemError && error.status < 500 && error.code !== 'idempotency.in_flight') {
    keys.forget(payload)
  }
}
